import { isAbortError } from '@src/utility/error';
import { LogManager } from '@src/utility/Logger';

import type ApiClient from '../apiClient/ApiClient';
import type { TranscriptElement } from '../types.ts/transcript';
import type {
  CoverageVerdict,
  TranslationData,
  TranslationHandler,
  TranslationMemoryReport,
} from '../types.ts/translation';
import type {
  CompositionPhraseTranslation,
  CompositionTranslationOutcome,
  TranslatableTarget,
} from '../videoTranscriptionProcessor/translation/compositionTypes';
import type { Client } from '@datocms/cma-client-browser';
import type { GraphQLClient } from 'graphql-request';

const GENERATE_ENDPOINT = '/api/aiprompts/translations-generate';
const SAVE_ENDPOINT = '/api/aiprompts/translations-save';
const REGENERATE_ENDPOINT = '/api/aiprompts/translations-regenerate';
const REMOVE_ENDPOINT = '/api/aiprompts/translations-remove';
const LIST_ENDPOINT = '/api/aiprompts/translations-list';
const GET_ENDPOINT = '/api/aiprompts/translations-get';
const SRT_UPLOAD_ENDPOINT = '/api/aiprompts/translations-srt-upload';
const SPLIT_ENDPOINT = '/api/aiprompts/translations-split';
const COVERAGE_CHECK_ENDPOINT = '/api/aiprompts/translations-coverage-check';
const RETRANSLATE_SPAN_ENDPOINT =
  '/api/aiprompts/translations-retranslate-span';

const logger = LogManager.getLogger('repository:TranslationRepository');

function getApiBase(): string {
  return process.env.REACT_APP_API_URL ?? '';
}

/**
 * Typed error thrown when the server responds non-2xx. Preserves the
 * server's structured `{ error, code, lineNumber }` so callers (analytics,
 * UI surfaces) can read fields directly instead of regex-parsing the
 * `.message`. The legacy message format (`{context} failed: {status} —
 * {error} ({code})`) is retained so existing tests and toast text still
 * work.
 */
export class TranslationApiError extends Error {
  readonly status: number;
  readonly statusText: string;
  readonly code?: string;
  readonly lineNumber?: number;

  constructor(init: {
    context: string;
    status: number;
    statusText: string;
    serverError?: string;
    code?: string;
    lineNumber?: number;
  }) {
    const detail = init.serverError
      ? init.code
        ? `${init.serverError} (${init.code})`
        : init.serverError
      : '';
    const suffix = detail ? ` — ${detail}` : '';
    super(`${init.context} failed: ${init.status} ${init.statusText}${suffix}`);
    this.name = 'TranslationApiError';
    this.status = init.status;
    this.statusText = init.statusText;
    this.code = init.code;
    this.lineNumber = init.lineNumber;
  }
}

async function throwIfNotOk(
  response: Response,
  context: string,
): Promise<void> {
  if (response.ok) return;
  let serverError: string | undefined;
  let code: string | undefined;
  let lineNumber: number | undefined;
  let rawBody = '';
  try {
    rawBody = await response.text();
    if (rawBody) {
      try {
        const parsed = JSON.parse(rawBody);
        if (parsed && typeof parsed.error === 'string') {
          serverError = parsed.error;
          if (typeof parsed.code === 'string') code = parsed.code;
          if (typeof parsed.lineNumber === 'number') {
            lineNumber = parsed.lineNumber;
          }
        } else {
          serverError = rawBody;
        }
      } catch {
        serverError = rawBody;
      }
    }
  } catch {
    // body already consumed or unreadable; fall back to status line
  }
  throw new TranslationApiError({
    context,
    status: response.status,
    statusText: response.statusText,
    serverError,
    code,
    lineNumber,
  });
}

/**
 * Shared POST helper: serializes JSON, calls fetch with the abort signal,
 * runs `throwIfNotOk`, and returns the parsed body as `unknown`. Each
 * caller still owns response-shape narrowing (`ensurePhrasesArray` etc.)
 * because shapes diverge per endpoint.
 */
async function postJson(
  url: string,
  body: unknown,
  context: string,
  signal: AbortSignal | undefined,
): Promise<unknown> {
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    signal,
  });
  await throwIfNotOk(response, context);
  return response.json();
}

// Defense-in-depth: strip em dashes from translation output even though the
// prompts (PLAT-9691) instruct both providers not to emit them. Models are
// imperfect; this guarantees em-dash-free phrases regardless. Comma-space
// matches the natural conversational pause an em dash signals and is
// grammatical in every target language we serve.
export function stripEmDashes(text: string): string {
  return text.replace(/\s*—\s*/g, ', ');
}

// Narrow shape check only — verifies the response is an object with a
// `phrases[]` array. Full field-level validation is deferred to Epic 2 when
// reconciliation needs it. The returned value is typed as TranslationData
// because callers will treat it as such; callers accessing fields beyond
// `phrases` bear the risk until full validation lands.
/**
 * PLAT-9967 — repository response shape for `generateTranslation`. Adds the
 * optional `compositions` block alongside the `TranslationData` so the store
 * can drive per-section UI without re-parsing the wire body. `translation`
 * also carries the new optional `compositionPhrases` / `compositionHandler`
 * fields when the backend stamped them.
 */
export interface GenerateTranslationResponse {
  translation: TranslationData;
  /**
   * Backend's per-section composition translation outcome. Undefined when
   * the request body omitted `compositionTargets` or the backend ran in
   * `skipCompositions` mode. Present and `status: 'ok'` on success;
   * present and `status: 'error'` when the composition pipeline failed
   * but the transcript pipeline succeeded.
   */
  compositions?: CompositionTranslationOutcome;
  /**
   * PLAT-10924 — run-level translation-memory QA outcome. Absent when the run
   * applied no glossary entry (and on pre-PLAT-10924 backends). Read once by the
   * caller to fire the `translation_memory_qa_*` events; never persisted.
   *
   * Generate only: no regenerate scope produces a report today — the transcript
   * scopes load no TM (PLAT-10875) and the composition path has no QA
   * reconciliation.
   */
  translationMemoryReport?: TranslationMemoryReport;
}

/**
 * PLAT-10046 — repository response shape for `regenerateTranslation`.
 * Mirrors `GenerateTranslationResponse` so the store can react to the
 * `compositions` block identically across the two endpoints.
 *
 * Transcript-scope regenerate (`scope: 'full' | 'partial'`) ignores the
 * `compositions` field — backend omits it; this shape simply tolerates the
 * absence so callers can read it uniformly without scope-aware branching.
 */
export interface RegenerateTranslationResponse {
  translation: TranslationData;
  /**
   * Present and `status: 'ok'` on a successful `scope: 'compositions'`
   * call. Absent for transcript-scope regenerate.
   */
  compositions?: CompositionTranslationOutcome;
}

/**
 * PLAT-9964 wire shape. Narrows the optional top-level `compositions`
 * block without throwing — pre-PLAT-9964 backends omit it entirely, and
 * `skipCompositions: true` paths also drop it.
 */
function extractCompositionsBlock(
  value: unknown,
): CompositionTranslationOutcome | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const raw = (value as { compositions?: unknown }).compositions;
  if (!raw || typeof raw !== 'object') return undefined;
  const status = (raw as { status?: unknown }).status;
  if (status === 'ok') {
    const phrasesAdded = (raw as { phrasesAdded?: unknown }).phrasesAdded;
    const phrasesSkippedRaw = (raw as { phrasesSkipped?: unknown })
      .phrasesSkipped;
    const phrasesOrphaned = (raw as { phrasesOrphaned?: unknown })
      .phrasesOrphaned;
    const handler = (raw as { handler?: unknown }).handler;
    const pinned =
      phrasesSkippedRaw &&
      typeof phrasesSkippedRaw === 'object' &&
      typeof (phrasesSkippedRaw as { pinned?: unknown }).pinned === 'number'
        ? (phrasesSkippedRaw as { pinned: number }).pinned
        : 0;
    return {
      status: 'ok',
      phrasesAdded: typeof phrasesAdded === 'number' ? phrasesAdded : 0,
      phrasesSkipped: { pinned },
      phrasesOrphaned:
        typeof phrasesOrphaned === 'number' ? phrasesOrphaned : 0,
      handler:
        handler && typeof handler === 'object'
          ? (handler as TranslationHandler)
          : undefined,
    };
  }
  if (status === 'error') {
    const code = (raw as { code?: unknown }).code;
    const message = (raw as { message?: unknown }).message;
    return {
      status: 'error',
      code: typeof code === 'string' ? code : 'UNKNOWN',
      message:
        typeof message === 'string'
          ? message
          : 'Composition translation failed',
    };
  }
  return undefined;
}

/**
 * PLAT-10924 wire shape. Narrows the optional top-level
 * `translationMemoryReport` block without throwing — a no-TM run and any
 * pre-PLAT-10924 backend omit it entirely. `retried` / `rescued` default to `[]`
 * so a backend mid-deploy (report present, retry unions not yet) reads as "no
 * retries" rather than crashing the counts.
 */
function extractTmReportBlock(
  value: unknown,
): TranslationMemoryReport | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const raw = (value as { translationMemoryReport?: unknown })
    .translationMemoryReport;
  if (!raw || typeof raw !== 'object') return undefined;
  const ids = (key: string): string[] => {
    const list = (raw as Record<string, unknown>)[key];
    return Array.isArray(list)
      ? list.filter((id): id is string => typeof id === 'string')
      : [];
  };
  const missedRaw = (raw as { missed?: unknown }).missed;
  const missed = Array.isArray(missedRaw)
    ? (missedRaw.filter(
        term => term && typeof term === 'object',
      ) as TranslationMemoryReport['missed'])
    : [];
  return {
    honored: ids('honored'),
    missed,
    unverifiable: ids('unverifiable'),
    retried: ids('retried'),
    rescued: ids('rescued'),
  };
}

function extractCompositionPhrases(
  value: unknown,
): CompositionPhraseTranslation[] | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const raw = (value as { compositionPhrases?: unknown }).compositionPhrases;
  if (!Array.isArray(raw)) return undefined;
  // PLAT-10046 — normalize optional metadata fields the backend may send
  // back as `null` (older serializers, or fields that were never written).
  // Downstream consumers compare `originalTranslatedValue !== undefined`
  // to decide whether a baseline is captured; a `null` would slip past
  // that check and render `null` as the "Original translation" in the
  // pin-detail card. Drop nulls to undefined so the contract holds.
  return raw.map(entry => {
    if (!entry || typeof entry !== 'object') return entry;
    const e = entry as Partial<CompositionPhraseTranslation> & {
      originalTranslatedValue?: string | null;
      pinnedAt?: string | null;
      pinnedBy?: string | null;
    };
    const cleaned = { ...e };
    if (cleaned.originalTranslatedValue == null) {
      delete cleaned.originalTranslatedValue;
    }
    if (cleaned.pinnedAt == null) delete cleaned.pinnedAt;
    if (cleaned.pinnedBy == null) delete cleaned.pinnedBy;
    return cleaned as CompositionPhraseTranslation;
  }) as CompositionPhraseTranslation[];
}

function extractCompositionHandler(
  value: unknown,
): TranslationHandler | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const raw = (value as { compositionHandler?: unknown }).compositionHandler;
  if (!raw || typeof raw !== 'object') return undefined;
  return raw as TranslationHandler;
}

function ensurePhrasesArray(value: unknown, context: string): TranslationData {
  if (
    !value ||
    typeof value !== 'object' ||
    !Array.isArray((value as { phrases?: unknown }).phrases)
  ) {
    throw new Error(`${context}: response missing phrases[] array`);
  }
  const obj = value as { changelog?: unknown };
  // Older persisted translations predate the changelog field. The save
  // endpoint requires `changelog: TranslationChange[]`, so normalize on load
  // to guarantee every in-memory TranslationData has the array. Return a
  // shallow clone rather than mutating the parsed body in place.
  const changelog = Array.isArray(obj.changelog) ? obj.changelog : [];
  // Strip em dashes from translation fields. Source-language fields
  // (`originalValueSnapshot`) are intentionally left alone — em dashes
  // there are part of the source, not our concern.
  const data = value as TranslationData;
  const phrases = data.phrases.map(p => ({
    ...p,
    translatedValue: stripEmDashes(p.translatedValue),
    originalTranslatedValue:
      p.originalTranslatedValue !== undefined
        ? stripEmDashes(p.originalTranslatedValue)
        : p.originalTranslatedValue,
  }));
  return { ...data, changelog, phrases } as TranslationData;
}

function ensureCoverageShape(value: unknown): {
  coveragePercent: number;
  uncoveredOriginalIndices: number[];
  warnings: string[];
} {
  // Tolerate a missing block (older clients won't see it) — default to
  // full coverage with no warnings so callers can read fields safely.
  if (value == null) {
    return {
      coveragePercent: 1,
      uncoveredOriginalIndices: [],
      warnings: [],
    };
  }
  const v = value as {
    coveragePercent?: unknown;
    uncoveredOriginalIndices?: unknown;
    warnings?: unknown;
  };
  return {
    coveragePercent:
      typeof v.coveragePercent === 'number' ? v.coveragePercent : 1,
    uncoveredOriginalIndices: Array.isArray(v.uncoveredOriginalIndices)
      ? v.uncoveredOriginalIndices.filter(
          (n): n is number => typeof n === 'number',
        )
      : [],
    warnings: Array.isArray(v.warnings)
      ? v.warnings.filter((s): s is string => typeof s === 'string')
      : [],
  };
}

// Narrow the span coverage verdict (PLAT-10267 / PLAT-10268). Distinct from
// `ensureCoverageShape` above (the SRT-upload 0–1 shape) — do NOT reuse it.
// The endpoint never throws; any malformed / empty body degrades to a
// non-blocking `covered`/100 verdict so a bad response can never gate a save.
const COVERED_VERDICT: CoverageVerdict = {
  coveragePercent: 100,
  status: 'covered',
  explanation: '',
};

function ensureCoverageVerdict(value: unknown): CoverageVerdict {
  if (!value || typeof value !== 'object') {
    return { ...COVERED_VERDICT };
  }
  const v = value as {
    coveragePercent?: unknown;
    status?: unknown;
    missing?: unknown;
    added?: unknown;
    explanation?: unknown;
  };
  if (
    typeof v.coveragePercent !== 'number' ||
    Number.isNaN(v.coveragePercent)
  ) {
    return { ...COVERED_VERDICT };
  }
  if (v.status !== 'covered' && v.status !== 'partial' && v.status !== 'over') {
    return { ...COVERED_VERDICT };
  }
  // Clamp to the documented 0–100 range so a model slip can't drive the badge
  // out of band.
  const coveragePercent = Math.min(100, Math.max(0, v.coveragePercent));
  const verdict: CoverageVerdict = {
    coveragePercent,
    status: v.status,
    explanation: typeof v.explanation === 'string' ? v.explanation : '',
  };
  if (typeof v.missing === 'string') verdict.missing = v.missing;
  if (typeof v.added === 'string') verdict.added = v.added;
  return verdict;
}

function ensureStoredAtShape(value: unknown): {
  latest: string;
  original: string;
  upload: string;
} {
  const v = (value ?? {}) as {
    latest?: unknown;
    original?: unknown;
    upload?: unknown;
  };
  return {
    latest: typeof v.latest === 'string' ? v.latest : '',
    original: typeof v.original === 'string' ? v.original : '',
    upload: typeof v.upload === 'string' ? v.upload : '',
  };
}

export class TranslationRepository {
  private dClient: Client | ApiClient;
  private gqlClient: GraphQLClient;

  constructor(dClient: Client | ApiClient, gqlClient: GraphQLClient) {
    this.dClient = dClient;
    this.gqlClient = gqlClient;
  }

  async fetchTranslation(url: string): Promise<TranslationData> {
    try {
      const response = await fetch(url);
      await throwIfNotOk(response, 'TranslationRepository.fetchTranslation');
      const data = (await response.json()) as unknown;
      return ensurePhrasesArray(data, 'TranslationRepository.fetchTranslation');
    } catch (error) {
      logger.error('fetchTranslation failed', { url, error });
      throw error;
    }
  }

  /** Language codes that already have a `latest.json` persisted for this story. */
  async listTranslations(storyId: string): Promise<string[]> {
    const url = `${getApiBase()}${LIST_ENDPOINT}?storyId=${encodeURIComponent(storyId)}`;
    try {
      const response = await fetch(url);
      await throwIfNotOk(response, 'TranslationRepository.listTranslations');
      const body = (await response.json()) as { languages?: unknown };
      if (!body || !Array.isArray(body.languages)) {
        throw new Error(
          'TranslationRepository.listTranslations: response missing languages[] array',
        );
      }
      return body.languages.filter(
        (x: unknown): x is string => typeof x === 'string',
      );
    } catch (error) {
      logger.error('listTranslations failed', { storyId, error });
      throw error;
    }
  }

  /**
   * Fetch a persisted `latest.json` by (storyId, language) via the server
   * proxy. Used by discovery on page load so the toggle lists previously-
   * generated languages without a direct S3 URL.
   *
   * Returns `null` when the translation doesn't exist (404), so callers
   * iterating over a language list don't have to catch per-entry.
   */
  async fetchTranslationByLanguage(
    storyId: string,
    language: string,
  ): Promise<TranslationData | null> {
    const url =
      `${getApiBase()}${GET_ENDPOINT}?storyId=${encodeURIComponent(storyId)}` +
      `&language=${encodeURIComponent(language)}`;
    try {
      const response = await fetch(url);
      if (response.status === 404) return null;
      await throwIfNotOk(
        response,
        'TranslationRepository.fetchTranslationByLanguage',
      );
      const data = (await response.json()) as unknown;
      return ensurePhrasesArray(
        data,
        'TranslationRepository.fetchTranslationByLanguage',
      );
    } catch (error) {
      logger.error('fetchTranslationByLanguage failed', {
        storyId,
        language,
        error,
      });
      throw error;
    }
  }

  /**
   * On-demand fine-split (PLAT-10269 / T9). SAVE-BASED: the endpoint re-splits
   * the SAVED translation server-side and writes `latest.json` (bumps version,
   * appends `split` changelog, preserves provenance). `phraseIndices` are
   * POSITIONS into the stored `phrases[]` for `scope: 'segment'`. Returns
   * nothing — the caller refetches the authoritative result with
   * `fetchTranslationByLanguage`.
   */
  async splitTranslation(params: {
    storyId: string;
    sourceLanguage: string;
    targetLanguage: string;
    scope: 'whole' | 'segment';
    phraseIndices?: number[];
    signal?: AbortSignal;
  }): Promise<void> {
    const { signal, ...body } = params;
    await postJson(
      `${getApiBase()}${SPLIT_ENDPOINT}`,
      body,
      'TranslationRepository.splitTranslation',
      signal,
    );
  }

  /**
   * Span-level coverage check (PLAT-10267 / PLAT-10268). POSTs the edited
   * span pair to the live endpoint and returns a `CoverageVerdict`. The
   * endpoint never throws — a model/parse failure degrades server-side to a
   * `covered`/100 verdict — and `ensureCoverageVerdict` mirrors that on the
   * client, so a malformed body resolves to the same non-blocking fallback
   * rather than rejecting. A transport failure (non-2xx) still rejects via
   * `postJson` so callers can surface a coverage-request error without
   * blocking the save.
   */
  async checkCoverage(params: {
    storyId: string;
    sourceLanguage: string;
    targetLanguage: string;
    originalSpan: string;
    translatedSpan: string;
    signal?: AbortSignal;
  }): Promise<CoverageVerdict> {
    const { signal, ...body } = params;
    const data = await postJson(
      `${getApiBase()}${COVERAGE_CHECK_ENDPOINT}`,
      body,
      'TranslationRepository.checkCoverage',
      signal,
    );
    return ensureCoverageVerdict(data);
  }

  /**
   * Directed re-translation of a single span (PLAT-10257). POSTs the span's
   * SOURCE transcript elements plus an optional free-text steering
   * `instruction` to the stateless PLAT-10256 endpoint and returns the
   * re-translated string. The endpoint persists nothing — the caller builds
   * the reversible `origin:'directed'` override segment from the returned
   * value, so a failure here can never corrupt stored data. `instruction`
   * stays optional: the stale re-translate path (PLAT-10266) reuses this
   * method with it omitted. A malformed 200 body throws rather than
   * returning garbage; non-2xx responses throw `TranslationApiError` with
   * `status`/`code` via `postJson`.
   */
  async directedRetranslateSpan(params: {
    storyId: string;
    sourceLanguage: string;
    targetLanguage: string;
    elements: TranscriptElement[];
    instruction?: string;
    signal?: AbortSignal;
  }): Promise<string> {
    const { signal, ...body } = params;
    const data = await postJson(
      `${getApiBase()}${RETRANSLATE_SPAN_ENDPOINT}`,
      body,
      'TranslationRepository.directedRetranslateSpan',
      signal,
    );
    const translatedValue = (data as { translatedValue?: unknown } | null)
      ?.translatedValue;
    if (typeof translatedValue !== 'string') {
      throw new Error(
        'TranslationRepository.directedRetranslateSpan: response missing translatedValue string',
      );
    }
    return translatedValue;
  }

  async generateTranslation(params: {
    storyId: string;
    /**
     * The showcase the user is translating in (PLAT-10875). Forwarded so the
     * backend loads that workspace's translation memory even when the story
     * belongs to multiple showcases. Validated backend-side.
     */
    showcaseId?: string;
    /**
     * Optional. If omitted (preferred), the server reads the canonical
     * transcription from S3 via storyId + sourceLanguage. Only pass elements
     * when the client has diverged from S3 and needs to override the source.
     */
    elements?: TranscriptElement[];
    sourceLanguage: string;
    targetLanguage: string;
    handler?: TranslationHandler;
    signal?: AbortSignal;
    /**
     * Story title + summary. LLM handlers fold this into their system
     * prompt so the translated output carries the right register and
     * domain terminology. Provider-only handlers ignore.
     */
    storyContext?: { title?: string; summary?: string };
    /**
     * PLAT-9967. Plugin-pre-extracted translatable composition targets
     * (see `extractTranslatableElements`). When present and non-empty, the
     * backend runs the composition pipeline alongside the transcript pass
     * and returns a top-level `compositions` block on the response. Omit
     * (or pass empty) for transcript-only translation.
     */
    compositionTargets?: TranslatableTarget[];
    /**
     * PLAT-9967. Existing per-language composition phrases the client has
     * in memory. When supplied, the backend skips reading `latest.json`
     * for the composition-merge pass. Pass-through; the backend owns
     * merge + orphan-cleanup semantics.
     */
    existingCompositionPhrases?: CompositionPhraseTranslation[];
  }): Promise<GenerateTranslationResponse> {
    const {
      signal,
      handler,
      compositionTargets,
      existingCompositionPhrases,
      ...rest
    } = params;
    // Server accepts `handlerInternalName: string` (the handler record's
    // internal identifier, e.g. 'openai-simple-v1' — current default per
    // the modal's SOURCES wiring), not the full handler object. When
    // omitted, the server resolves from the showcase default.
    // Only attach composition fields when the caller supplied them so the
    // request body for transcript-only flows stays byte-identical to the
    // pre-PLAT-9967 shape.
    const body = {
      ...rest,
      ...(handler ? { handlerInternalName: handler.id } : {}),
      ...(compositionTargets && compositionTargets.length > 0
        ? { compositionTargets }
        : {}),
      ...(existingCompositionPhrases ? { existingCompositionPhrases } : {}),
    };
    try {
      const data = await postJson(
        `${getApiBase()}${GENERATE_ENDPOINT}`,
        body,
        'TranslationRepository.generateTranslation',
        signal,
      );
      const translation = ensurePhrasesArray(
        data,
        'TranslationRepository.generateTranslation',
      );
      // PLAT-9964 added an optional top-level `compositions` block carrying
      // per-section status. Pre-PLAT-9964 backends and `skipCompositions`
      // paths omit it; we narrow without throwing so the transcript half
      // is unaffected by composition-side changes.
      const compositions = extractCompositionsBlock(data);
      // PLAT-9964 also stamps `compositionPhrases` / `compositionHandler`
      // at the top level of the wire blob; surface them on the returned
      // TranslationData so the store can persist them per language.
      const compositionPhrases = extractCompositionPhrases(data);
      const compositionHandler = extractCompositionHandler(data);
      return {
        translation: {
          ...translation,
          ...(compositionPhrases !== undefined ? { compositionPhrases } : {}),
          ...(compositionHandler !== undefined ? { compositionHandler } : {}),
        },
        compositions,
        // PLAT-10924 — run-level TM QA outcome, for the qa_retry / qa_failed
        // analytics events. Absent on no-TM runs.
        translationMemoryReport: extractTmReportBlock(data),
      };
    } catch (error) {
      // AbortError is a user action, not a failure — surface without logging.
      if (isAbortError(error)) throw error;
      logger.error('generateTranslation failed', {
        storyId: params.storyId,
        sourceLanguage: params.sourceLanguage,
        targetLanguage: params.targetLanguage,
        handlerId: params.handler?.id,
        compositionTargetCount: params.compositionTargets?.length ?? 0,
        error,
      });
      throw error;
    }
  }

  /**
   * Destructive re-translation. The server archives the prior `original.json`
   * + `latest.json` before rewriting them and returns the fresh phrase list
   * in the same shape as `generateTranslation`. Uses handler defaults on the
   * server side (no per-call overrides from this call site for now).
   *
   * PLAT-10046: `scope: 'compositions'` runs the composition pipeline only —
   * the transcript half is untouched. Backend uses `compositionTargets` for
   * LLM context and merges results against `existingCompositionPhrases`
   * (escape hatch identical to `generateTranslation`). `force: true` discards
   * AI-generated phrases for the supplied `sourceTexts`; pinned phrases
   * (`pinnedOverrideByManualEdit`) clear too. Preset-locked rows always win
   * over runtime regardless of force. `compositionElementId` / `sourceTexts`
   * are telemetry tags (PLAT-9965 variants B / C) — backend logs them but
   * still operates on the full uncovered set.
   */
  async regenerateTranslation(params: {
    storyId: string;
    /**
     * The showcase the user is translating in (PLAT-10875); forwarded so the
     * backend loads that workspace's translation memory. Validated backend-side.
     * Only the `compositions` scope loads TM today — the transcript scopes
     * ignore it.
     */
    showcaseId?: string;
    sourceLanguage: string;
    targetLanguage: string;
    scope?: 'full' | 'partial' | 'compositions';
    phraseIndices?: number[];
    handler?: TranslationHandler;
    signal?: AbortSignal;
    /** Story title + summary. See `generateTranslation`. */
    storyContext?: { title?: string; summary?: string };
    /** PLAT-10256 free-text steering (whole/partial scope); sent only when non-blank. */
    instruction?: string;
    /**
     * PLAT-10046. Plugin-pre-extracted translatable composition targets
     * (see `extractTranslatableElements`). Required for `scope:
     * 'compositions'`; ignored under `'full' | 'partial'`.
     */
    compositionTargets?: TranslatableTarget[];
    /**
     * PLAT-10046. Existing per-language composition phrases the client has
     * in memory. When supplied under `scope: 'compositions'`, the backend
     * skips reading `latest.json` for the merge pass. Ignored under
     * `'full' | 'partial'`.
     */
    existingCompositionPhrases?: CompositionPhraseTranslation[];
    /**
     * PLAT-10046. `true` discards AI phrase translations + pinned manual
     * edits matching `sourceTexts` (or every composition phrase if
     * `sourceTexts` is omitted) and re-runs the LLM. Preset-locked rows
     * are untouched. Only honoured under `scope: 'compositions'`.
     */
    force?: boolean;
    /**
     * PLAT-10046 telemetry tag (PLAT-9965 variant B). Backend logs the
     * triggering composition so per-composition retranslate analytics line
     * up across surfaces. Backend still operates on the full uncovered
     * sourceText set. Ignored unless `scope: 'compositions'`.
     */
    compositionElementId?: string;
    /**
     * PLAT-10046 telemetry tag (PLAT-9965 variant C). Restricts the force
     * pass to a specific set of sourceTexts (e.g. the per-composition
     * sources the user is retranslating). Ignored unless `scope:
     * 'compositions'`. Omit for "retranslate every uncovered phrase".
     */
    sourceTexts?: string[];
    /**
     * PLAT-10112 — Variant-D advisory tag for a standalone text-overlay
     * retranslate. Backend logs it alongside the `kind: 'text'` target.
     * Ignored unless `scope: 'compositions'`.
     */
    textElementId?: string;
  }): Promise<RegenerateTranslationResponse> {
    const {
      signal,
      handler,
      instruction,
      compositionTargets,
      existingCompositionPhrases,
      force,
      compositionElementId,
      sourceTexts,
      textElementId,
      ...rest
    } = params;
    const scope = rest.scope ?? 'full';
    const isCompositions = scope === 'compositions';
    // Composition-only fields are guarded so transcript-scope bodies stay
    // byte-identical to the pre-PLAT-10046 shape.
    const body = {
      ...rest,
      scope,
      ...(handler ? { handlerInternalName: handler.id } : {}),
      // PLAT-10256 free-text steering. Trimmed and included only when non-blank
      // so whitespace-only instructions stay byte-identical to an absent one.
      ...(instruction && instruction.trim()
        ? { instruction: instruction.trim() }
        : {}),
      ...(isCompositions && compositionTargets && compositionTargets.length > 0
        ? { compositionTargets }
        : {}),
      ...(isCompositions && existingCompositionPhrases
        ? { existingCompositionPhrases }
        : {}),
      ...(isCompositions && force !== undefined ? { force } : {}),
      ...(isCompositions && compositionElementId
        ? { compositionElementId }
        : {}),
      ...(isCompositions && sourceTexts ? { sourceTexts } : {}),
      ...(isCompositions && textElementId ? { textElementId } : {}),
    };
    try {
      const data = await postJson(
        `${getApiBase()}${REGENERATE_ENDPOINT}`,
        body,
        'TranslationRepository.regenerateTranslation',
        signal,
      );
      const translation = ensurePhrasesArray(
        data,
        'TranslationRepository.regenerateTranslation',
      );
      // PLAT-10046 — backend stamps an optional `compositions` block on
      // scope-compositions responses; transcript-scope responses omit it
      // (the existing `ensurePhrasesArray` validator tolerates extra fields
      // either way).
      const compositions = extractCompositionsBlock(data);
      const compositionPhrases = extractCompositionPhrases(data);
      const compositionHandler = extractCompositionHandler(data);
      return {
        translation: {
          ...translation,
          ...(compositionPhrases !== undefined ? { compositionPhrases } : {}),
          ...(compositionHandler !== undefined ? { compositionHandler } : {}),
        },
        compositions,
      };
    } catch (error) {
      if (isAbortError(error)) throw error;
      // PLAT-10046 — only include composition-related fields when the
      // request actually carried them. Transcript-only scopes
      // (`'full'` / `'partial'`) get the original byte-identical log
      // shape; `'compositions'` adds the composition cardinality.
      const isCompositionsLog = params.scope === 'compositions';
      logger.error('regenerateTranslation failed', {
        storyId: params.storyId,
        sourceLanguage: params.sourceLanguage,
        targetLanguage: params.targetLanguage,
        scope: params.scope,
        sourceTextsCount: params.sourceTexts?.length ?? 0,
        force: params.force,
        handlerId: params.handler?.id,
        ...(isCompositionsLog
          ? {
              compositionTargetCount: params.compositionTargets?.length ?? 0,
              ...(params.compositionElementId
                ? { compositionElementId: params.compositionElementId }
                : {}),
            }
          : {}),
        error,
      });
      throw error;
    }
  }

  /**
   * Install a customer-uploaded SRT as the translation for `(storyId,
   * targetLanguage)`. Mirrors `generateTranslation`'s shape but routes
   * through the `import-srt-v1` backend handler (PLAT-9778). The server
   * persists the raw SRT for audit, runs timestamp-based alignment + the
   * existing sentence-splitter post-process, and writes `latest.json`
   * (not `original.json` — that's the AI baseline owned by Generate).
   *
   * Returns the canonical translation plus a coverage block the caller
   * uses to decide whether to surface a partial-coverage warning.
   */
  async uploadSrtTranslation(params: {
    storyId: string;
    sourceLanguage: string;
    targetLanguage: string;
    /** Original filename — preserved verbatim in audit metadata. */
    filename: string;
    /** UTF-8 SRT contents. */
    srtBody: string;
    signal?: AbortSignal;
  }): Promise<{
    data: TranslationData;
    coverage: {
      coveragePercent: number;
      uncoveredOriginalIndices: number[];
      warnings: string[];
    };
    storedAt: { latest: string; original: string; upload: string };
  }> {
    const { signal, ...body } = params;
    try {
      const raw = (await postJson(
        `${getApiBase()}${SRT_UPLOAD_ENDPOINT}`,
        body,
        'TranslationRepository.uploadSrtTranslation',
        signal,
      )) as { coverage?: unknown; storedAt?: unknown };
      // Reuse the shared phrase-shape validator + em-dash strip; it returns
      // a TranslationData even though the backend response also carries
      // `coverage` and `storedAt` siblings (those pass through untouched).
      const data = ensurePhrasesArray(
        raw,
        'TranslationRepository.uploadSrtTranslation',
      );
      const coverage = ensureCoverageShape(raw.coverage);
      const storedAt = ensureStoredAtShape(raw.storedAt);
      return { data, coverage, storedAt };
    } catch (error) {
      if (isAbortError(error)) throw error;
      logger.error('uploadSrtTranslation failed', {
        storyId: params.storyId,
        targetLanguage: params.targetLanguage,
        filename: params.filename,
        error,
      });
      throw error;
    }
  }

  /**
   * Destructive deletion of a translation. The server archives the prior
   * `original.json` + `latest.json` to a timestamped prefix on S3 before
   * deleting the live blobs.
   *
   * Returns `null` when the server reports `TRANSLATION_NOT_FOUND` (404).
   * Callers treat that as success — the desired end state (no live
   * translation in this language) is already reached, so the eviction
   * path runs identically. This makes multi-tab races idempotent.
   */
  async removeTranslation(params: {
    storyId: string;
    targetLanguage: string;
    sourceLanguage?: string;
    signal?: AbortSignal;
  }): Promise<{ archivedPath: string; phrasesRemoved: number } | null> {
    const { signal, ...body } = params;
    try {
      // Remove keeps inline fetch handling because the 404 → null branch
      // is endpoint-specific (the shared helper always throws on non-2xx).
      const response = await fetch(`${getApiBase()}${REMOVE_ENDPOINT}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        signal,
      });
      if (response.status === 404) return null;
      await throwIfNotOk(response, 'TranslationRepository.removeTranslation');
      const data = (await response.json()) as {
        archivedPath?: unknown;
        phrasesRemoved?: unknown;
      };
      const archivedPath =
        typeof data?.archivedPath === 'string' ? data.archivedPath : '';
      const phrasesRemoved =
        typeof data?.phrasesRemoved === 'number' ? data.phrasesRemoved : 0;
      return { archivedPath, phrasesRemoved };
    } catch (error) {
      if (isAbortError(error)) throw error;
      logger.error('removeTranslation failed', {
        storyId: params.storyId,
        targetLanguage: params.targetLanguage,
        error,
      });
      throw error;
    }
  }

  async saveTranslation(params: {
    storyId: string;
    targetLanguage: string;
    translationData: TranslationData;
  }): Promise<{ url: string }> {
    try {
      const body = (await postJson(
        `${getApiBase()}${SAVE_ENDPOINT}`,
        params,
        'TranslationRepository.saveTranslation',
        undefined,
      )) as { url?: unknown };
      if (!body || typeof body.url !== 'string') {
        throw new Error(
          'TranslationRepository.saveTranslation: response missing url string',
        );
      }
      return { url: body.url };
    } catch (error) {
      logger.error('saveTranslation failed', {
        storyId: params.storyId,
        targetLanguage: params.targetLanguage,
        error,
      });
      throw error;
    }
  }
}
